import React, { useEffect, useState, useMemo } from 'react';
import { Container, Button, Form, Alert, Spinner, ListGroup, Badge, Row, Col, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../firebase';
import { collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../../components/context/AuthContext';
import { useNotification } from '../../../components/ui/Notification';
import Layout from '../../../components/ui/Layout';
import { createLogger } from '../../../utils/logger';
import {
  DEFAULT_PRICE_TAX_MODE,
  DEFAULT_TAX_CATEGORY,
  TAX_CATEGORIES,
  getDefaultTaxRateForCategory,
} from '../../../constants/jamaicaGct';

const logger = createLogger('CreateInventory');

const CreateInventory = () => {
  const { currentUser, userRole, employeeSupermarketId } = useAuth();
  const { showNotification } = useNotification();
  const [supermarkets, setSupermarkets] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedSupermarketId, setSelectedSupermarketId] = useState('');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [taxCategory, setTaxCategory] = useState(DEFAULT_TAX_CATEGORY);
  const [customTaxRate, setCustomTaxRate] = useState('');
  const [quantity, setQuantity] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [existingInventory, setExistingInventory] = useState([]);

  // Discount deals state
  const [discountDeals, setDiscountDeals] = useState([]);
  const [dealQuantity, setDealQuantity] = useState('');
  const [dealPrice, setDealPrice] = useState('');

  const navigate = useNavigate();

  const isEmployeeLimited = userRole === 'Employee' && employeeSupermarketId;
  const selectedSupermarket = useMemo(
    () => supermarkets.find((supermarket) => supermarket.id === selectedSupermarketId),
    [supermarkets, selectedSupermarketId]
  );
  const selectedPriceTaxMode = selectedSupermarket?.price_tax_mode || DEFAULT_PRICE_TAX_MODE;
  const isTaxInclusivePricing = selectedPriceTaxMode === DEFAULT_PRICE_TAX_MODE;

  // Filter and paginate products based on search term
  const filteredProducts = useMemo(() => {
    let filteredList = products;

    if (productSearchTerm.trim()) {
      const rawTerm = productSearchTerm.trim();
      const termLower = rawTerm.toLowerCase();
      filteredList = products.filter(product =>
        product.display_name.toLowerCase().includes(termLower) ||
        (product.upc && product.upc.includes(rawTerm)) ||
        (product.description && product.description.toLowerCase().includes(termLower))
      );
    }

    return filteredList.slice(0, 15);
  }, [products, productSearchTerm]);

  const handleProductSelect = (product) => {
    // Check if product is already in inventory
    if (checkIfProductAlreadyInInventory(product.id)) {
      showNotification({
        type: 'warning',
        title: 'Product Already in Inventory',
        message: 'This product has already been added to your supermarket\'s inventory. To update the quantity or price, please use the Edit Inventory option instead.'
      });
      setShowProductDropdown(false);
      return;
    }

    setSelectedProduct(product);
    setSelectedProductId(product.id);
    setProductSearchTerm(product.display_name);
    setShowProductDropdown(false);
  };

  const handleProductSearchChange = (e) => {
    const value = e.target.value;
    setProductSearchTerm(value);
    setShowProductDropdown(true);
    if (!value.trim()) {
      setSelectedProduct(null);
      setSelectedProductId('');
    }
  };

  const checkIfProductAlreadyInInventory = (productId) => {
    if (!selectedSupermarketId) return false;
    return existingInventory.some(inventoryItem =>
      inventoryItem.supermarket_id === selectedSupermarketId &&
      inventoryItem.product_id === productId
    );
  };

  // Discount deals handlers
  const addDiscountDeal = () => {
    if (!dealQuantity || !dealPrice) {
      showNotification({
        type: 'warning',
        title: 'Missing Fields',
        message: 'Please enter both quantity and price for the discount deal.'
      });
      return;
    }

    const quantityNum = parseInt(dealQuantity);
    const priceNum = parseFloat(dealPrice);

    if (isNaN(quantityNum) || quantityNum <= 0) {
      showNotification({
        type: 'warning',
        title: 'Invalid Quantity',
        message: 'Please enter a valid positive quantity.'
      });
      return;
    }

    if (isNaN(priceNum) || priceNum <= 0) {
      showNotification({
        type: 'warning',
        title: 'Invalid Price',
        message: 'Please enter a valid positive price.'
      });
      return;
    }

    // Check if deal with same quantity already exists
    if (discountDeals.some(deal => deal.quantity === quantityNum)) {
      showNotification({
        type: 'warning',
        title: 'Duplicate Deal',
        message: 'A discount deal with this quantity already exists.'
      });
      return;
    }

    // Validate that deal price is less than regular price * quantity
    const regularPrice = parseFloat(salePrice);
    if (priceNum >= regularPrice * quantityNum) {
      showNotification({
        type: 'warning',
        title: 'Invalid Deal Price',
        message: 'Deal price should be less than the regular price for the same quantity.'
      });
      return;
    }

    const newDeal = {
      id: Date.now(), // Simple ID for local state management
      quantity: quantityNum,
      price: priceNum
    };

    setDiscountDeals([...discountDeals, newDeal]);
    setDealQuantity('');
    setDealPrice('');
  };

  const removeDiscountDeal = (dealId) => {
    setDiscountDeals(discountDeals.filter(deal => deal.id !== dealId));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProductDropdown && !event.target.closest('.product-search-container')) {
        setShowProductDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProductDropdown]);

  useEffect(() => {
    const fetchData = async () => {
      setPageLoading(true);
      try {
        const supermarketsSnapshot = await getDocs(collection(db, 'Supermarkets'));
        let supermarketsList = supermarketsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        if (isEmployeeLimited) {
          supermarketsList = supermarketsList.filter(s => s.id === employeeSupermarketId);
          if (supermarketsList.length > 0) {
            setSelectedSupermarketId(employeeSupermarketId);
          } else {
            setError("Your assigned supermarket could not be found. Please contact an admin.");
          }
        } else if (userRole === 'Employee' && !employeeSupermarketId) {
            showNotification({
              type: 'error',
              title: 'Access Denied',
              message: 'You are not assigned to a supermarket. Cannot add inventory.'
            });
        }
        setSupermarkets(supermarketsList);

        const productsSnapshot = await getDocs(collection(db, 'Products'));
        const productsList = productsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProducts(productsList);

      } catch (err) {
        setError(`Failed to fetch initial data. ${  err.message}`);
        showNotification({
          type: 'error',
          title: 'Error',
          message: `Failed to fetch initial data. ${err.message}`
        });
        logger.error(err);
      }
      setPageLoading(false);
    };
    if (currentUser) {
        fetchData();
    }
  }, [currentUser, userRole, employeeSupermarketId, isEmployeeLimited, showNotification]);

  // Fetch existing inventory when supermarket changes
  useEffect(() => {
    const fetchExistingInventory = async () => {
      if (!selectedSupermarketId) {
        setExistingInventory([]);
        return;
      }

      try {
        const inventorySnapshot = await getDocs(collection(db, 'Inventory'));
        const inventoryList = inventorySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Filter inventory for the selected supermarket
        const supermarketInventory = inventoryList.filter(item =>
          item.supermarket_id === selectedSupermarketId
        );

        setExistingInventory(supermarketInventory);
      } catch (err) {
        logger.error('Failed to fetch existing inventory:', err);
        setExistingInventory([]);
      }
    };

    fetchExistingInventory();
  }, [selectedSupermarketId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!selectedSupermarketId || !selectedProductId || !salePrice || !quantity) {
      showNotification({
        type: 'warning',
        title: 'Missing Fields',
        message: 'Please fill in all fields.'
      });
      return;
    }
    if (userRole === 'Employee' && !employeeSupermarketId) {
        showNotification({
          type: 'error',
          title: 'Access Denied',
          message: 'Cannot add inventory: You are not assigned to a supermarket.'
        });
        return;
    }
    if (isEmployeeLimited && selectedSupermarketId !== employeeSupermarketId) {
        showNotification({
          type: 'error',
          title: 'Access Denied',
          message: 'You can only add inventory for your assigned supermarket.'
        });
        return;
    }

    // Check if product is already in inventory
    if (checkIfProductAlreadyInInventory(selectedProductId)) {
        showNotification({
          type: 'warning',
          title: 'Product Already in Inventory',
          message: 'This product has already been added to your supermarket\'s inventory. To update the quantity or price, please use the Edit Inventory option instead.'
        });
        return;
    }

    const supermarket = supermarkets.find(s => s.id === selectedSupermarketId);
    const product = products.find(p => p.id === selectedProductId);

    if (!supermarket || !product) {
        showNotification({
          type: 'error',
          title: 'Invalid Selection',
          message: 'Selected supermarket or product details are invalid. Please refresh.'
        });
        return;
    }

    setLoading(true);
    try {
      // Validate data before sending to Firestore
      const salePriceNum = parseFloat(salePrice);
      const quantityNum = parseInt(quantity);

      if (isNaN(salePriceNum) || salePriceNum <= 0) {
        throw new Error('Invalid sale price. Please enter a valid positive number.');
      }

      if (isNaN(quantityNum) || quantityNum <= 0) {
        throw new Error('Invalid quantity. Please enter a valid positive number.');
      }

      if (!selectedSupermarketId || !selectedProductId) {
        throw new Error('Missing supermarket or product selection.');
      }

      if (!currentUser?.uid) {
        throw new Error('User authentication required.');
      }

      let taxRate = getDefaultTaxRateForCategory(taxCategory);
      if (taxCategory === 'custom') {
        const customTaxRateNum = parseFloat(customTaxRate);
        if (isNaN(customTaxRateNum) || customTaxRateNum < 0 || customTaxRateNum > 100) {
          throw new Error('Custom GCT rate must be a number from 0 to 100.');
        }
        taxRate = customTaxRateNum / 100;
      }

      await addDoc(collection(db, 'Inventory'), {
        supermarket_id: selectedSupermarketId,
        supermarket_name: supermarket.display_name || 'Unknown Supermarket',
        product_id: selectedProductId,
        product_name: product.display_name || 'Unknown Product',
        product_upc: product.upc || null,
        sale_price: salePriceNum,
        price_tax_mode: selectedPriceTaxMode,
        sale_price_includes_tax: isTaxInclusivePricing,
        tax_category: taxCategory,
        tax_rate: taxRate,
        quantity: quantityNum,
        discount_deals: discountDeals.length > 0 ? discountDeals.map(deal => ({
          quantity: deal.quantity,
          price: deal.price
        })) : [],
        created_by_uid: currentUser.uid,
        created_by_email: currentUser.email || 'Unknown User',
        created_at: serverTimestamp(),
      });

      setSelectedProductId('');
      setSelectedProduct(null);
      setProductSearchTerm('');
      setSalePrice('');
      setTaxCategory(DEFAULT_TAX_CATEGORY);
      setCustomTaxRate('');
      setQuantity('');
      setDiscountDeals([]);

      showNotification({
        type: 'success',
        title: 'Success',
        message: 'Inventory item added successfully!'
      });

      // Refresh existing inventory to update the product list
      const updatedInventory = [...existingInventory, {
        supermarket_id: selectedSupermarketId,
        product_id: selectedProductId,
        product_name: product.display_name,
        sale_price: parseFloat(salePrice),
        quantity: parseInt(quantity),
      }];
      setExistingInventory(updatedInventory);

    } catch (err) {
      showNotification({
        type: 'error',
        title: 'Error',
        message: `Failed to add product to inventory. ${err.message}`
      });
      logger.error(err);
    }
    setLoading(false);
  };

  const handleGoBack = () => navigate(-1);

  if (pageLoading && !currentUser) {
    return (
      <Layout>
        <Container className="px-3">
          <p>Loading user data...</p>
        </Container>
      </Layout>
    );
  }
  if (userRole === 'Employee' && !employeeSupermarketId && !pageLoading) {
      return (
          <Layout>
              <Container className="mt-3 px-3">
                  <Button variant="outline-secondary" onClick={handleGoBack} className="mb-3">&larr; Go Back</Button>
                  <Alert variant="danger">You are not assigned to any supermarket. Please contact an administrator to add inventory.</Alert>
              </Container>
          </Layout>
      );
  }

  return (
    <Layout>
      <Container className="px-3">
         <Button variant="outline-secondary" onClick={handleGoBack} className="mt-3 mb-3">
          &larr; Go Back
        </Button>
        <h1 className="mb-3 text-break">Add Item to Inventory</h1>
        {error && <Alert variant="danger">{error}</Alert>}

        {pageLoading ? (
          <div className="text-center">
            <Spinner animation="border" className="mb-3" />
            <p>Loading form data...</p>
          </div>
        ) : (
            <Form onSubmit={handleSubmit}>
            <Form.Group controlId="supermarketSelect" className="mb-3">
                <Form.Label>Select Supermarket</Form.Label>
                <Form.Control
                as="select"
                value={selectedSupermarketId}
                onChange={(e) => setSelectedSupermarketId(e.target.value)}
                required
                disabled={isEmployeeLimited}
                className="w-100"
                >
                {userRole === 'Admin' && <option value="">Select Supermarket</option>}
                {supermarkets.map((supermarket) => (
                    <option key={supermarket.id} value={supermarket.id}>
                    {supermarket.display_name}
                    </option>
                ))}
                {isEmployeeLimited && supermarkets.length === 0 && <option value="">Your assigned supermarket data is unavailable</option>}
                </Form.Control>
            </Form.Group>

            <Form.Group controlId="productSelect" className="mb-3">
                <Form.Label>Select Product</Form.Label>
                {selectedSupermarketId && existingInventory.length > 0 && (
                    <div className="mb-2">
                        <small className="text-muted">
                            {existingInventory.length} product{existingInventory.length !== 1 ? 's' : ''} already in inventory.
                            Products already added will be marked with a warning badge.
                        </small>
                    </div>
                )}
                <div className="position-relative product-search-container">
                    <div className="position-relative">
                        <Form.Control
                            type="text"
                            placeholder="Search by product name, UPC, or description..."
                            value={productSearchTerm}
                            onChange={handleProductSearchChange}
                            onFocus={() => setShowProductDropdown(true)}
                            required
                            className="w-100"
                            style={{ fontSize: '16px' }} // Prevents zoom on iOS
                        />
                        {selectedProduct && (
                            <Button
                                variant="outline-secondary"
                                onClick={() => {
                                    setSelectedProduct(null);
                                    setSelectedProductId('');
                                    setProductSearchTerm('');
                                }}
                                style={{
                                    fontSize: '16px',
                                    minWidth: '44px',
                                    position: 'absolute',
                                    right: '0',
                                    top: '0',
                                    height: '100%',
                                    borderTopLeftRadius: '0',
                                    borderBottomLeftRadius: '0',
                                    zIndex: 10
                                }}
                            >
                                ×
                            </Button>
                        )}
                    </div>

                    {showProductDropdown && (
                        <div
                            className="position-absolute w-100 bg-white border rounded-bottom shadow-sm"
                            style={{
                                zIndex: 1000,
                                maxHeight: '300px',
                                overflowY: 'auto',
                                top: '100%',
                                maxWidth: '100vw'
                            }}
                        >
                            {filteredProducts.length > 0 ? (
                                <ListGroup variant="flush">
                                    {filteredProducts.map((product) => {
                                        const isAlreadyInInventory = checkIfProductAlreadyInInventory(product.id);
                                        return (
                                            <ListGroup.Item
                                                key={product.id}
                                                action
                                                onClick={() => handleProductSelect(product)}
                                                className="cursor-pointer p-3"
                                                style={{ cursor: 'pointer' }}
                                                disabled={isAlreadyInInventory}
                                            >
                                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-2">
                                                    <div className="flex-grow-1 w-100">
                                                        <div className="fw-semibold text-break fs-6">
                                                            {product.display_name}
                                                        </div>
                                                        {product.upc && (
                                                            <div className="small text-muted mt-1">
                                                                UPC: {product.upc}
                                                            </div>
                                                        )}
                                                        {product.description && (
                                                            <div className="small text-muted text-break mt-1">
                                                                {product.description.length > 50
                                                                    ? `${product.description.substring(0, 50)}...`
                                                                    : product.description
                                                                }
                                                            </div>
                                                        )}
                                                        {isAlreadyInInventory && (
                                                            <div className="small text-warning mt-2">
                                                                <i className="fas fa-exclamation-triangle me-1"></i>
                                                                Already in inventory - use edit option
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="d-flex flex-wrap align-items-start gap-1">
                                                        {product.category && (
                                                            <Badge bg="secondary" className="flex-shrink-0 small">
                                                                {product.category}
                                                            </Badge>
                                                        )}
                                                        {isAlreadyInInventory && (
                                                            <Badge bg="warning" text="dark" className="flex-shrink-0 small">
                                                                Added
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </ListGroup.Item>
                                        );
                                    })}
                                </ListGroup>
                            ) : (
                                <div className="p-4 text-center text-muted">
                                    <div className="small mb-2">
                                        {productSearchTerm.trim()
                                            ? 'No products found'
                                            : 'Start typing to search products'
                                        }
                                    </div>
                                </div>
                            )}
                            {selectedProduct && (
                                <div className="border-top p-3">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => {
                                            setSelectedProduct(null);
                                            setSelectedProductId('');
                                            setProductSearchTerm('');
                                            setShowProductDropdown(false);
                                        }}
                                        className="w-100"
                                    >
                                        Cancel Selection
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                {selectedProduct && (
                    <div className="mt-2 p-3 bg-light rounded border">
                        <div className="d-flex flex-column gap-2">
                            <div>
                                <small className="text-muted">Selected: </small>
                                <span className="fw-semibold text-break">{selectedProduct.display_name}</span>
                            </div>
                            {selectedProduct.description && (
                                <div className="small text-muted text-break">
                                    {selectedProduct.description}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Form.Group>

            <Form.Group controlId="salePrice" className="mb-3">
                <Form.Label>Sale Price ({isTaxInclusivePricing ? 'tax-inclusive where applicable' : 'before GCT'})</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  placeholder={isTaxInclusivePricing ? 'Enter shelf price including GCT where applicable' : 'Enter sale price before GCT'}
                  value={salePrice}
                  onChange={(e) => setSalePrice(e.target.value)}
                  required
                  className="w-100"
                  style={{ fontSize: '16px' }} // Prevents zoom on iOS
                />
                <Form.Text className="text-muted small">
                  Staff enter one price. This supermarket is configured for {selectedPriceTaxMode === DEFAULT_PRICE_TAX_MODE ? 'tax-inclusive' : 'tax-exclusive'} pricing.
                </Form.Text>
            </Form.Group>

            <Row>
                <Col md={taxCategory === 'custom' ? 6 : 12}>
                    <Form.Group controlId="taxCategory" className="mb-3">
                        <Form.Label>Tax Category</Form.Label>
                        <Form.Select
                          value={taxCategory}
                          onChange={(e) => setTaxCategory(e.target.value)}
                          required
                          className="w-100"
                          style={{ fontSize: '16px' }}
                        >
                          {TAX_CATEGORIES.map((category) => (
                            <option key={category.value} value={category.value}>
                              {category.label}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted small">
                          Select the Jamaica GCT treatment for this product. Use custom only when a product-specific rate is needed.
                        </Form.Text>
                    </Form.Group>
                </Col>
                {taxCategory === 'custom' && (
                  <Col md={6}>
                    <Form.Group controlId="customTaxRate" className="mb-3">
                      <Form.Label>Custom GCT Rate (%)</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        placeholder="Enter custom GCT rate"
                        value={customTaxRate}
                        onChange={(e) => setCustomTaxRate(e.target.value)}
                        required
                        className="w-100"
                        style={{ fontSize: '16px' }}
                      />
                    </Form.Group>
                  </Col>
                )}
            </Row>

            <Form.Group controlId="quantity" className="mb-3">
                <Form.Label>Quantity</Form.Label>
                <Form.Control
                  type="number"
                  step="1"
                  placeholder="Enter quantity"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  required
                  className="w-100"
                  style={{ fontSize: '16px' }} // Prevents zoom on iOS
                />
            </Form.Group>

            {/* Discount Deals Section */}
            <Form.Group controlId="discountDeals" className="mb-3">
                <Form.Label>Discount Deals (Optional)</Form.Label>
                <small className="text-muted d-block mb-2">
                  Create bulk purchase discounts using the same {isTaxInclusivePricing ? 'tax-inclusive' : 'tax-exclusive'} pricing mode. For example: Buy 3 items for $8.00 instead of $12.00
                </small>
                <Card className="mb-3">
                    <Card.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Control
                                    type="number"
                                    step="1"
                                    placeholder="Quantity for deal"
                                    value={dealQuantity}
                                    onChange={(e) => setDealQuantity(e.target.value)}
                                    className="w-100"
                                    style={{ fontSize: '16px' }}
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    placeholder="Price for deal"
                                    value={dealPrice}
                                    onChange={(e) => setDealPrice(e.target.value)}
                                    className="w-100"
                                    style={{ fontSize: '16px' }}
                                />
                            </Col>
                        </Row>
                        <Button
                            variant="outline-primary"
                            onClick={addDiscountDeal}
                            className="mt-2"
                            disabled={!dealQuantity || !dealPrice}
                        >
                            Add Deal
                        </Button>
                    </Card.Body>
                </Card>

                {/* Display existing deals */}
                {discountDeals.length > 0 && (
                    <div className="mb-3">
                        <h6>Current Discount Deals:</h6>
                        <ListGroup>
                            {discountDeals.map((deal) => {
                                const regularPrice = parseFloat(salePrice) * deal.quantity;
                                const savings = regularPrice - deal.price;
                                return (
                                    <ListGroup.Item key={deal.id} className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <strong>{deal.quantity} items</strong> for <strong>${deal.price.toFixed(2)}</strong>
                                            <br />
                                            <small className="text-success">
                                                Save ${savings.toFixed(2)} (${(deal.price / deal.quantity).toFixed(2)} per item)
                                            </small>
                                        </div>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => removeDiscountDeal(deal.id)}
                                        >
                                            Remove
                                        </Button>
                                    </ListGroup.Item>
                                );
                            })}
                        </ListGroup>
                    </div>
                )}
            </Form.Group>

            <Button
              type="submit"
              variant="primary"
              className="w-100 mt-4 py-3"
              disabled={loading || (userRole === 'Employee' && !employeeSupermarketId)}
              style={{ fontSize: '16px' }}
            >
                {loading ? (
                  <div className="d-flex align-items-center justify-content-center">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Adding...
                  </div>
                ) : (
                  'Add to Inventory'
                )}
            </Button>
            </Form>
        )}
      </Container>
    </Layout>
  );
};

export default CreateInventory;
